// @flow

import React, { useState, useEffect, useRef } from 'react';
import { Liquid } from 'liquidjs';

import { log } from '../../utils/jsUtils';

import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import ToggleButton from '@mui/lab/ToggleButton';
import ToggleButtonGroup from '@mui/lab/ToggleButtonGroup';
import Popper from '@mui/material/Popper';
import Grow from '@mui/material/Grow';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import InputBase from '@mui/material/InputBase';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import InfoIcon from '@mui/icons-material/Info';

import { createClasses } from '../../utils/stylesUtils';
import liquidMessageEditorStyles from './Styles/LiquidMessageEditor.Style';

import type { Tenant } from '../../services/SPBackend';

type Props = {
  recipients: Array<Tenant>,
  message: string,
  setMessage: (message: string) => void,
  emptyErrorMsg: () => void,
  isSending: boolean,
  creditsUsed?: number,
  isBroadcast?: boolean
}

const engine = new Liquid();

const LiquidMessageEditor = (props: Props) => {
  const {
    recipients,
    message,
    setMessage,
    emptyErrorMsg,
    isSending,
    creditsUsed = 0,
    isBroadcast = true
  } = props;

  const theme = useTheme();
  const classes = createClasses(liquidMessageEditorStyles, theme);

  const textareaRef = useRef();
  const btn1AnchorRef = useRef();

  const [previewToggleBtnValue, setPreviewToggleBtnValue] = useState('editor');
  const [msgPreview, setMsgPreview] = useState('');
  const [isMsgPreviewError, setIsMsgPreviewError] = useState(false);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [refocusOnNewCursorPosition, setRefocusOnNewCursorPosition] = useState(0);
  const [openCustomerOptions, setOpenCustomerOptions] = useState(false);
  
  const handlePreviewToggleBtns = (e, newSelection) => {
    if (!isBroadcast) return;
    log('[handlePreviewToggleBtns] newSelection: ', newSelection);
    setPreviewToggleBtnValue(newSelection);
  };

  useEffect(() => {
    // Only render preview when preview tab is active (not while typing in editor)
    if (previewToggleBtnValue !== 'preview') {
      return;
    }

    const renderPreview = async() => {
      try {
        const [recipient] = recipients;
        log('recipient: ', recipient);
        const output = await engine.parseAndRender(message, recipient);
        log('output: ', output);
        setMsgPreview(output);
        setIsMsgPreviewError(false);
      } catch (e) {
        log('[liquid render error] ', e);
        log('[liquid render error] e.message', e.message);
        setMsgPreview(e.message);
        setIsMsgPreviewError(true);
      }
    };
    renderPreview();
  }, [recipients, message, previewToggleBtnValue]);

  const handleCursorPositionChange = (e) => {
    log('[handleCursorPositionChange]...');
    log('[handleTextChange] new cursor Position: ', e.target.selectionStart);
    setCursorPosition(e.target.selectionStart);
  };

  const handleTextChange = (e) => {
    log('[handleTextChange]...');
    if (message.length <= 1600) {
      setMessage(e.target.value);
    }
  };

  useEffect(() => {
    if (refocusOnNewCursorPosition > 0) {
      textareaRef.current.selectionStart = cursorPosition;
      textareaRef.current.selectionEnd = cursorPosition;
      log('textareaRef.current2: ', textareaRef.current.selectionStart);
      textareaRef.current.focus();

    }
  }, [refocusOnNewCursorPosition]);

  const insertVariable = (varName) => {
    const textBeforeCursorPosition = message.substring(0, cursorPosition);
    const textAfterCursorPosition = message.substring(cursorPosition, message.length);
    log('[insertVariable] cursorPosition: ', cursorPosition);
    log('[insertVariable] textBeforeCursorPosition: ', textBeforeCursorPosition);
    log('[insertVariable] textAfterCursorPosition: ', textAfterCursorPosition);
      
    setMessage(`${textBeforeCursorPosition}{{${varName}}}${textAfterCursorPosition}`);
    setCursorPosition(cursorPosition + varName.length + 4);
    setRefocusOnNewCursorPosition(refocusOnNewCursorPosition + 1);
  };

  const handleToggleCustomerOptions = (force = null) => {
    if (force !==null) {
      setOpenCustomerOptions(force);  
    }
    setOpenCustomerOptions(!openCustomerOptions);
  };


  function handleListKeyDown(event) {
    if (event.key === 'Tab') {
      event.preventDefault();
      setOpenCustomerOptions(false);
    }
  }

  // Calculate characters remaining in current credit bucket
  const charsPerCredit = parseInt(process.env.REACT_APP_CHARS_NB_PER_CREDIT || '67', 10);
  const charsRemaining = message.length > 0 && message.length < 1600
    ? charsPerCredit - (message.length % charsPerCredit)
    : 0;

  return (
    <Box id='MessageTenantDialog'>
      {isBroadcast && (
        <ToggleButtonGroup
          sx={{
            ...classes.toggleNavButtons,
            ...classes.secondInput
          }}
          color="primary"
          exclusive
          onChange={handlePreviewToggleBtns}
          value={previewToggleBtnValue}
        >
          <ToggleButton
            sx={{
              ...classes.navBtnDeselected,
              '&.Mui-selected': classes.navBtnSelected
            }}
            value="editor"
          >Editor</ToggleButton>
          <ToggleButton
            sx={{
              ...classes.navBtnDeselected,
              ...(isMsgPreviewError ? classes.error : {}),
              '&.Mui-selected': classes.navBtnSelected
            }}
            value="preview"
          >Preview</ToggleButton>
        </ToggleButtonGroup>
      )}
      {/* <div className={classes.toolbar}>
              <Button onClick={() => insertVariable('FirstName')}>FirstName</Button>
      </div> */}
      <Box sx={classes.inputCtr}>
      {
        previewToggleBtnValue === 'preview'
        ? <Box sx={classes.previewZone}>
            {
              isMsgPreviewError
              ? <Box component="span" sx={classes.error}>Syntax error: {msgPreview}</Box>
              : msgPreview
            }
          </Box>
        : <Box sx={classes.EditZone}>
            <Paper
              sx={{
                ...classes.textAreaCtr,
                ...classes.squareTheme,
                ...classes.simpleBorder
              }}
              component="div"
            >
              {isBroadcast && (
                <>
                  <Box sx={classes.toolbarCtr}>
                    <Tooltip
                      placement='top-end'
                      title={'Insert a dynamic field'}
                    >
                      <Button
                        aria-controls={openCustomerOptions ? 'menu-list-grow' : undefined}
                        aria-haspopup="true"
                        sx={classes.toolbarMenuBtn}
                        endIcon={<ArrowDropDownIcon />}
                        onClick={handleToggleCustomerOptions}
                        ref={btn1AnchorRef}
                      >
                        Dynamic Content
                      </Button>
                    </Tooltip>
                    <Box sx={classes.grow} />
                  </Box>
                  <Popper
                    anchorEl={btn1AnchorRef.current}
                    sx={{
                      ...classes.menuList,
                      ...classes.squareTheme
                    }}
                    disablePortal={false}
                    open={openCustomerOptions}
                    transition
                  >
                    {({ TransitionProps, placement }) => (
                      <Grow
                        {...TransitionProps}
                        style={{ transformOrigin: placement === 'bottom' ? 'left top' : 'left bottom' }}
                      >
                        <Paper sx={classes.squareTheme}>
                          <ClickAwayListener onClickAway={() => handleToggleCustomerOptions(false)}>
                            <MenuList autoFocusItem={openCustomerOptions}
                                      id="menu-list-grow"
                                      onKeyDown={handleListKeyDown}
                            >
                              <MenuItem sx={classes.menuTitle}>Customer Info</MenuItem>
                              <MenuItem onClick={() => {handleToggleCustomerOptions(false); insertVariable('FirstName');}}>First Name</MenuItem>
                              <MenuItem onClick={() => {handleToggleCustomerOptions(false); insertVariable('LastName');}}>Last Name</MenuItem>
                              <MenuItem onClick={() => {handleToggleCustomerOptions(false); insertVariable('Email');}}>Email Address</MenuItem>
                            </MenuList>
                          </ClickAwayListener>
                        </Paper>
                      </Grow>
                    )}
                  </Popper>
                </>
              )}
              <InputBase
                sx={{
                  ...classes.largeTextField,
                  ...classes.squareTheme
                }}
                id='message'
                inputProps={{ 'aria-label': 'message text' }}
                inputRef={textareaRef}
                multiline
                onBlur={handleCursorPositionChange}
                onChange={handleTextChange}
                onClick={handleCursorPositionChange}
                onFocus={(e) => {
                  // Scroll textarea into view on mobile when keyboard appears
                  setTimeout(() => {
                    e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }, 300);
                }}
                placeholder='Write your text message here'
                readOnly={isSending}
                rows={10}
                value={message}
              />
            </Paper>
            {/* Character counter */}
            {process.env.REACT_APP_CHARS_NB_PER_CREDIT && (
              <Tooltip
                title={`${creditsUsed} ${creditsUsed === 1 ? 'Credit' : 'Credits'}`}
                placement="top-end"
              >
                <Box sx={classes.characterCounterCtr}>
                  {message.length >= 1600 ? (
                    <Typography sx={classes.warningText}>
                      1600 characters max per message.
                    </Typography>
                  ) : message.length > 0 ? (
                    <Typography sx={classes.characterCounter}>
                      {charsRemaining} / {creditsUsed}
                    </Typography>
                  ) : (
                    <Typography sx={classes.characterCounter}>&nbsp;</Typography>
                  )}
                </Box>
              </Tooltip>
            )}
          </Box>
      }
      </Box>
    </Box>
  );
};

export default LiquidMessageEditor;
